import { useState } from 'react';
import { Button, Empty, Input, Spin } from 'antd';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useCollections, useCreateCollection } from '@presentation/hooks/useCollections';
import { CollectionTreeNode } from '@presentation/components/workbench/CollectionTreeNode';
import { NamePromptModal } from '@presentation/components/workbench/NamePromptModal';
import { ImportCollectionModal } from '@presentation/components/workbench/ImportCollectionModal';

interface CollectionSidebarProps {
  workspaceId: string;
}

export const CollectionSidebar = ({ workspaceId }: CollectionSidebarProps) => {
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const { data: collections, isLoading } = useCollections(workspaceId);
  const createCollection = useCreateCollection(workspaceId);

  const filtered = collections?.filter((collection) =>
    collection.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-black/10 p-2 dark:border-white/10">
        <Input.Search
          placeholder="Search collections"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          allowClear
          size="small"
        />
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          onClick={() => setImporting(true)}
          aria-label="Import"
          title="Import"
        />
        <Button
          type="text"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setCreating(true)}
          aria-label="New collection"
        />
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {isLoading && (
          <div className="flex justify-center py-6">
            <Spin size="small" />
          </div>
        )}

        {!isLoading && filtered?.length === 0 && (
          <Empty
            className="mt-6"
            description={search ? 'No collections match your search' : 'No collections yet'}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {!search && (
              <Button type="primary" size="small" onClick={() => setCreating(true)}>
                New collection
              </Button>
            )}
          </Empty>
        )}

        {filtered?.map((collection) => (
          <CollectionTreeNode
            key={collection.id}
            workspaceId={workspaceId}
            collection={collection}
          />
        ))}
      </div>

      <NamePromptModal
        open={creating}
        title="New collection"
        confirmLoading={createCollection.isPending}
        onCancel={() => setCreating(false)}
        onSubmit={(name) =>
          createCollection.mutate({ name }, { onSuccess: () => setCreating(false) })
        }
      />

      <ImportCollectionModal
        workspaceId={workspaceId}
        open={importing}
        onClose={() => setImporting(false)}
      />
    </div>
  );
};
